import React from 'react';

/** Шаг ползунка: дни поштучно не нужны, а 5 даёт удобные круглые значения. */
export const GRACE_STEP = 5;
/** Предел на случай старого бэкенда, который ещё не отдаёт `maxNewGraceDays`. */
export const GRACE_FALLBACK_MAX = 365;

interface GracePeriodSliderProps {
    /** Текущее значение, дней. */
    value: number;
    /** Верхняя граница диапазона — приходит с сервера, чтобы не разъехаться с валидацией. */
    max: number;
    /** Сколько вещей получат повод при этом значении; null — пересчёт ещё идёт. */
    flaggedCount: number | null;
    /** Сколько вещей попадёт в быстрый разбор. */
    quickCount: number | null;
    /** Пересчёт обзора в процессе — цифры приглушаются, но не исчезают. */
    recalculating: boolean;
    onChange: (value: number) => void;
}

/**
 * Ползунок льготы новизны в параметрах запуска разбора (feature declutter-grace-slider).
 *
 * <p>Льгота решает, что система считает поводом присмотреться к вещи: моложе выбранного возраста
 * — повода нет. На доступность полного разбора это не влияет (он берёт весь гардероб), но
 * определяет состав быстрого разбора, порядок очереди и цель сессии.</p>
 *
 * <p>Значение продублировано текстом рядом с ползунком: положение ручки само по себе величину
 * не сообщает, а на узком экране — тем более.</p>
 */
const GracePeriodSlider: React.FC<GracePeriodSliderProps> = ({
    value, max, flaggedCount, quickCount, recalculating, onChange,
}) => (
    <div className="mt-4" data-testid="declutter-grace">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
            <label className="block text-sm font-medium text-gray-900 dark:text-gray-100"
                   htmlFor="declutter-grace-slider">
                Не судить вещи моложе
            </label>
            <span className="text-sm font-semibold text-gray-900 dark:text-gray-100"
                  data-testid="declutter-grace-value">
                {value === 0 ? 'без ограничения' : `${value} дн.`}
            </span>
        </div>

        <input
            id="declutter-grace-slider"
            type="range"
            min={0}
            max={max}
            step={GRACE_STEP}
            value={value}
            onChange={e => onChange(Number(e.target.value))}
            aria-label="Льгота новизны, дней"
            aria-valuetext={value === 0 ? 'без ограничения' : `${value} дней`}
            className="mt-2 w-full max-w-md min-h-[44px] accent-brand-600 cursor-pointer"
            data-testid="declutter-grace-slider"
        />

        <p className="text-sm text-gray-500 dark:text-gray-400" data-testid="declutter-grace-hint">
            {value === 0
                ? 'Судим все вещи, даже вчерашние покупки.'
                : `Вещи в гардеробе меньше ${value} дней система не помечает поводом: они остаются `
                  + 'в полном разборе, но не попадают в быстрый и не формируют цель.'}
        </p>

        <p className={`mt-1 text-sm ${recalculating
            ? 'text-gray-400 dark:text-gray-500'
            : 'text-gray-600 dark:text-gray-300'}`}
           data-testid="declutter-grace-preview">
            Поводов: {flaggedCount ?? '—'} · в быстрый разбор: {quickCount ?? '—'}
        </p>
    </div>
);

export default GracePeriodSlider;
